import table from './countries.json';

/**
 * One country in the phone table: the ISO 3166-1 alpha-2 code, the English
 * name, the ITU dialling code (digits only, no `+`), the range of digits a
 * national number has AFTER the dialling code, and the trunk prefix people
 * dial at home before a national number (`0` in most of the world, `1` in
 * North America, `8` in Russia, nothing in Italy or Spain).
 *
 * The rows come from `countries.json`, which is the one source of this data.
 * `services/contacts/Services/PhoneNumbers.cs` carries a generated copy of
 * the same rows; the C# fixture test checks the two agree.
 */
export interface PhoneCountry {
  iso: string;
  name: string;
  dial: string;
  min: number;
  max: number;
  trunk: string;
  /** The flag as two regional-indicator symbols, derived from the ISO code. */
  flag: string;
}

/** The flag emoji for an ISO 3166-1 alpha-2 code ("ZA" → 🇿🇦). */
export function flagFor(iso: string): string {
  const up = iso.toUpperCase();
  if (!/^[A-Z]{2}$/.test(up)) return '';
  return String.fromCodePoint(0x1f1e6 + up.charCodeAt(0) - 65, 0x1f1e6 + up.charCodeAt(1) - 65);
}

/**
 * Every country, in table order. Where several countries share a dialling
 * code (+1, +7, +44, +61, +262 and so on) the one most people mean comes
 * first, so a pasted `+1…` reads as the United States unless the picker
 * already sits on another +1 country.
 */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = (table as Array<Omit<PhoneCountry, 'flag'>>).map((c) => ({
  ...c,
  flag: flagFor(c.iso)
}));

const BY_ISO = new Map(PHONE_COUNTRIES.map((c) => [c.iso, c]));

/** Longest dialling code in the table, so the matcher knows where to start. */
const LONGEST_DIAL = PHONE_COUNTRIES.reduce((n, c) => Math.max(n, c.dial.length), 1);

/** Look a country up by ISO code, case-insensitively. */
export function findCountry(iso: string | null | undefined): PhoneCountry | undefined {
  if (!iso) return undefined;
  return BY_ISO.get(iso.trim().toUpperCase());
}

/** Every country on one dialling code, primary first. */
export function countriesForDial(dial: string): PhoneCountry[] {
  return PHONE_COUNTRIES.filter((c) => c.dial === dial);
}

/**
 * The country whose dialling code starts `digits`, longest code first
 * (ITU codes are prefix-free, so `234` is tried before `23` before `2`).
 * `prefer` wins among countries on the same code, so a person on Canada
 * pasting `+1 416…` stays on Canada rather than flipping to the United
 * States.
 */
export function matchDial(digits: string, prefer?: PhoneCountry): PhoneCountry | undefined {
  for (let len = Math.min(LONGEST_DIAL, digits.length); len >= 1; len--) {
    const code = digits.slice(0, len);
    if (prefer && prefer.dial === code) return prefer;
    const hit = PHONE_COUNTRIES.find((c) => c.dial === code);
    if (hit) return hit;
  }
  return undefined;
}

/** Whether `n` digits is a national number length this country uses. */
export function fitsCountry(country: PhoneCountry, n: number): boolean {
  return n >= country.min && n <= country.max;
}
